<template>
  <settings-chrome
    kicker="Accounting"
    title="Payments"
    sub="Bank details for invoices, and card payments when people book."
    form-id="payments-settings-form"
    :dirty="dirty"
    :saving="isSaving"
    @discard="revert"
    @allow-leave="dirty = false"
  >
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="payments-settings-form"
        class="st-body"
        @submit.prevent="handleSubmit(submit)"
      >
        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Bank details</h2>
              <p class="st-lead">
                Shown on invoices so customers can pay you by transfer.
              </p>
            </div>
          </div>
          <div class="st-stack">
            <label class="st-field">
              Bank name
              <ValidationProvider rules="required|min:1" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.company.bankDetails.bankName"
                  :placeholder="$t('message.bankNamePlaceholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <label class="st-field">
              Account name
              <p class="st-hint">Must match the name on the account.</p>
              <ValidationProvider rules="required|min:1" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.company.bankDetails.accountName"
                  :placeholder="$t('message.bankAccountNamePlaceholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <label class="st-field">
              IBAN
              <ValidationProvider rules="required|bank_iban" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.company.bankDetails.iban"
                  :placeholder="$t('message.bankAccountIbanPlaceholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <div class="st-pair">
              <label class="st-field">
                BIC
                <ValidationProvider rules="required|bank_bic" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.company.bankDetails.bic"
                    :placeholder="$t('message.bankAccountBicPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="st-field">
                Account number
                <ValidationProvider rules="bank_account_numbers" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.company.bankDetails.accountNumber"
                    :placeholder="$t('message.bankAccountNumberPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
            <label class="st-field st-field-narrow">
              Sort code
              <p class="st-hint">Optional. Used for some Irish and UK banks.</p>
              <ValidationProvider rules="bank_sort_code" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.company.bankDetails.sortCode"
                  :placeholder="$t('message.bankAccountSortCodePlaceholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
          </div>
        </section>

        <section v-if="$can('read', 'ONLINE_PAYMENTS')" class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Card payments</h2>
              <p class="st-lead">
                Let people pay by card when they book. Stripe handles the card.
              </p>
            </div>
          </div>
          <div class="st-stack">
            <div class="st-field">
              <span>Take card payments</span>
              <div class="st-seg" role="radiogroup" aria-label="Take card payments">
                <button
                  type="button"
                  class="st-seg-btn"
                  :class="{ 'is-on': model.onlinePaymentsEnabled === true }"
                  @click="model.onlinePaymentsEnabled = true"
                >
                  On
                </button>
                <button
                  type="button"
                  class="st-seg-btn"
                  :class="{ 'is-on': !model.onlinePaymentsEnabled }"
                  @click="model.onlinePaymentsEnabled = false"
                >
                  Off
                </button>
              </div>
            </div>
            <div v-if="model.onlinePaymentsEnabled" class="st-field">
              <span>Stripe</span>
              <p v-if="!userSettings.stripeUserId" class="st-hint">
                We’ll send you to Stripe to create an account or connect one you
                already have.
              </p>
              <p v-else class="st-hint">
                Stripe is connected. You can take card payments.
              </p>
              <div class="st-seg">
                <a
                  v-if="!userSettings.stripeUserId"
                  class="st-chip is-primary"
                  :href="connectWithStripeLink"
                >
                  Connect Stripe
                </a>
                <button
                  v-else
                  class="st-chip"
                  type="button"
                  @click="onDisconnectStripe"
                >
                  Disconnect Stripe
                </button>
              </div>
            </div>
          </div>
        </section>

        <section v-if="$can('read', 'PAYMENT_REMINDERS')" class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Overdue emails</h2>
              <p class="st-lead">
                Email customers when an invoice is late.
              </p>
            </div>
          </div>
          <div class="st-field">
            <span>Send overdue emails</span>
            <div class="st-seg" role="radiogroup" aria-label="Send overdue emails">
              <button
                type="button"
                class="st-seg-btn"
                :class="{ 'is-on': model.paymentReminders.enabled === true }"
                @click="model.paymentReminders.enabled = true"
              >
                On
              </button>
              <button
                type="button"
                class="st-seg-btn"
                :class="{ 'is-on': model.paymentReminders.enabled === false }"
                @click="model.paymentReminders.enabled = false"
              >
                Off
              </button>
            </div>
          </div>
        </section>
      </form>
    </ValidationObserver>
  </settings-chrome>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { DEFAULT_SETTINGS } from "@/store/userSettings";
import env from "@/env";
import { generateUUID } from "@/helpers/helpers";
import SettingsChrome from "@/components/settings/SettingsChrome";

function mergeSettings(settings) {
  const base = settings || {};
  const company = base.company || {};
  return {
    ...DEFAULT_SETTINGS,
    ..._.cloneDeep(base),
    company: {
      ...DEFAULT_SETTINGS.company,
      ..._.cloneDeep(company),
      bankDetails: {
        ...DEFAULT_SETTINGS.company.bankDetails,
        ...(company.bankDetails || {})
      }
    },
    paymentReminders: {
      ...DEFAULT_SETTINGS.paymentReminders,
      ...(base.paymentReminders || {})
    }
  };
}

export default {
  components: { SettingsChrome },
  data() {
    const redirectUri = window.location.href;
    const stateToken = generateUUID();
    return {
      isSaving: false,
      dirty: false,
      hydrating: false,
      connectingWithStripe: false,
      model: mergeSettings({}),
      connectWithStripeLink: `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${env.stripe.clientId}&scope=read_write&state=${stateToken}&redirect_uri=${redirectUri}`
    };
  },
  computed: mapGetters(["userSettings"]),
  watch: {
    userSettings: {
      immediate: true,
      deep: true,
      handler() {
        if (this.dirty || this.isSaving) return;
        this.resetModel();
      }
    },
    model: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    }
  },
  created() {
    this.fetchUserSettings().then(this.handleAuthToken);
  },
  methods: {
    ...mapActions([
      "fetchUserSettings",
      "updateUserSettings",
      "connectWithStripe",
      "disconnectFromStripe"
    ]),
    resetModel() {
      this.hydrating = true;
      this.model = mergeSettings(this.userSettings);
      this.dirty = false;
      this.$nextTick(() => {
        this.hydrating = false;
      });
    },
    revert() {
      this.dirty = false;
      this.resetModel();
    },
    async submit() {
      this.isSaving = true;
      try {
        await this.updateUserSettings(this.model);
        this.dirty = false;
        this.resetModel();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.saveSettingsSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.saveSettingsFailure")
        });
      } finally {
        this.isSaving = false;
      }
    },
    async handleAuthToken() {
      if (!this.$route.query.code || this.userSettings.stripeUserId) return;
      this.connectingWithStripe = true;
      try {
        await this.connectWithStripe({
          authCode: this.$route.query.code
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.connectWithStripeFailure") +
            ` ${(error && error.message) || ""}`.trim()
        });
      } finally {
        this.connectingWithStripe = false;
        this.$router.replace({ path: "/settings/accounting/payments" });
      }
    },
    async onDisconnectStripe() {
      const confirmationResult = await this.$swal({
        title: this.$t("message.areYouSure"),
        text: this.$t("message.disconnectStripeConfirmationText"),
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: this.$t("message.goBack"),
        confirmButtonText: this.$t("message.confirm")
      });
      if (!confirmationResult.value) return;
      this.isSaving = true;
      try {
        await this.disconnectFromStripe();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.disconnectStripeSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.disconnectStripeFailure")
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>
